/**
 * Date formatting primitive shared by the user-preference formatters.
 *
 * The `timeZone` argument is the whole point of this module: it names the frame a
 * `Date` is READ in, and picking the wrong one shifts the rendered calendar day.
 * There are exactly two correct choices, and which one applies is decided by the
 * VALUE being rendered, not by the component doing the rendering:
 *
 *   * An INSTANT (a `TIMESTAMPTZ` — created_at, shipped_at, a meeting time) is a
 *     point on the world clock. Render it in the user's CONFIGURED zone, so they
 *     see the wall-clock time they'd have seen standing there.
 *
 *   * A DATE-ONLY value (a `DATE` — a planning start/end date, a due date) carries
 *     no instant at all. It is parsed to local midnight (`parseISO`) and written
 *     back from the local frame (`format(d, 'yyyy-MM-dd')`), so it must be READ in
 *     that same local frame — pass `timeZone: undefined` and `Intl` uses the
 *     ambient zone. The round-trip is then exact by construction, and the day shown
 *     always equals the day stored. Rendering such a value in the configured zone
 *     is what produced the roadmap's off-by-one-day labels.
 */

/**
 * `short` / `medium` / `long` / `full` spell a whole calendar date. The last three
 * are COMPACT styles that deliberately omit a component, for a surface with no
 * room for one — a chart's x-axis tick, a period label. They exist so such a
 * surface has a preference-driven formatter to reach for: the alternative
 * everybody reached for instead was
 * `toLocaleDateString(undefined, { month: 'short', day: 'numeric' })`, whose
 * `undefined` locale hands the decision to the runtime's AMBIENT locale — the
 * browser's OS setting, not the viewer's PanLuma `preferred_language` /
 * `date_format`.
 */
export type DateStyle =
  | 'short'
  | 'medium'
  | 'long'
  | 'full'
  | 'dayMonth'
  | 'monthYear'
  | 'monthYearShort';
export type DateFormatPattern = 'dd/mm/yyyy' | 'mm/dd/yyyy' | 'yyyy-mm-dd';

/**
 * The `Intl` fields each style asks for. Exported because the `useUserPreferences`
 * test doubles format from the same map — a double that re-types the mapping is a
 * second copy free to disagree with the provider it stands in for.
 */
export const DATE_STYLE_OPTIONS: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  short: { day: '2-digit', month: '2-digit', year: '2-digit' },
  medium: { day: '2-digit', month: '2-digit', year: 'numeric' },
  long: { day: 'numeric', month: 'long', year: 'numeric' },
  full: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
  dayMonth: { day: 'numeric', month: 'short' },
  monthYear: { month: 'short', year: 'numeric' },
  // A two-digit year, for an x-axis carrying a tick per month: "Jan 2026" is ~40%
  // wider than "Jan 26", and twelve of them is what decides whether a monthly
  // report's ticks fit. Reach for `monthYear` anywhere the year has room.
  monthYearShort: { month: 'short', year: '2-digit' },
};

/** A style that omits a component of the date, so it cannot render as a full ISO date. */
function isCompactStyle(style: DateStyle): boolean {
  return style === 'dayMonth' || style === 'monthYear' || style === 'monthYearShort';
}

/**
 * Format `date` per the user's date-format preference.
 *
 * @param timeZone IANA zone to read the date in, or `undefined` for the ambient
 *   browser frame (the correct choice for date-only values — see above).
 * @param languageLocale BCP-47 locale of the viewer's UI language, used ONLY by the
 *   styles that spell the month/weekday out. Omit for a language-agnostic render.
 */
export function formatDateInZone(
  date: Date,
  style: DateStyle,
  dateFormat: DateFormatPattern,
  timeZone: string | undefined,
  languageLocale?: string,
): string {
  if (isNaN(date.getTime())) return '';

  // ISO-like output is assembled from parts rather than a locale pattern, so it
  // is stable across engines. A COMPACT style has no whole date to spell, so it
  // takes the big-endian ORDER below instead (month before day).
  if (dateFormat === 'yyyy-mm-dd' && !isCompactStyle(style)) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const year = parts.find((p) => p.type === 'year')?.value ?? '';
    const month = parts.find((p) => p.type === 'month')?.value ?? '';
    const day = parts.find((p) => p.type === 'day')?.value ?? '';
    return `${year}-${month}-${day}`;
  }

  const options: Intl.DateTimeFormatOptions = { timeZone, ...DATE_STYLE_OPTIONS[style] };

  // Locale chosen purely for day/month ORDER — en-US is month-first, en-GB day-first.
  // A `yyyy-mm-dd` preference only reaches here on a compact style, and it is
  // big-endian, i.e. month-first like en-US.
  const orderLocale = dateFormat === 'dd/mm/yyyy' ? 'en-GB' : 'en-US';

  // 'long', 'full' and the two compact styles spell the month (and weekday) out, so
  // they must render in the viewer's LANGUAGE — an en-* anchor hands "14 August 2026"
  // to a Hebrew or Spanish user whose every other string on the page is translated,
  // and the anchor was only ever chosen to fix digit ORDER, which a worded date
  // settles for itself. English keeps the anchor: there the format preference is the
  // only thing that decides between "August 14, 2026" and "14 August 2026".
  const spellsOutMonth =
    style === 'long' || style === 'full' || isCompactStyle(style);
  const languageIsEnglish =
    !languageLocale || languageLocale.split('-')[0].toLowerCase() === 'en';
  const dateLocale = spellsOutMonth && !languageIsEnglish ? languageLocale : orderLocale;
  return new Intl.DateTimeFormat(dateLocale, options).format(date);
}

/**
 * Serialize a `Date` to a `yyyy-MM-dd` string for a DATE-only column, reading the
 * LOCAL calendar day.
 *
 * This is the WRITE-side counterpart to `formatPlanningDate`/`parseISO` on the read
 * side. NEVER use `date.toISOString().split('T')[0]` for a `DATE` value: `toISOString`
 * converts to UTC first, so a local-midnight `Date` in an east-of-UTC zone (e.g.
 * Asia/Tokyo, +9) rolls back to the previous calendar day — the user picks Jul 14 and
 * the API stores Jul 13. Formatting from the Date's LOCAL `getFullYear`/`getMonth`/
 * `getDate` keeps the day the user actually chose, in every timezone, and round-trips
 * exactly with `parseISO` on the read side.
 */
export function toDateOnlyString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse a DATE-only string (`yyyy-MM-dd`) into a `Date` at LOCAL midnight — the
 * READ-side counterpart to `toDateOnlyString`. `new Date("2026-07-14")` parses the
 * bare string as UTC midnight, so in any timezone behind UTC (e.g. America/New_York)
 * `.getDate()` rolls back to the previous calendar day (Jul 13) — the classic
 * date-only off-by-one. Building the `Date` from the y/m/d components keeps the
 * calendar day the value actually denotes, in every timezone, round-tripping with
 * `toDateOnlyString`. A value carrying a time component (a full ISO instant) has no
 * date-only ambiguity and is parsed as-is.
 */
export function parseDateOnlyToLocal(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match) {
    const [, year, month, day] = match.map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(value);
}
